import { Link } from 'react-router-dom'
import type { NaviStatus } from '@/api/warehouse'
import { cn } from '@/lib/utils'
import { NOTIZ_STREIFEN, SCHRAFFUR } from './navi-farben'

export interface WandReihe { reihe: string; lager: 1 | 2; plaetze: number; frei: number; belegt: number; wartet: number; notiz: number }

const HOEHE = 168

/** Signatur des Lager-Navi: jede Reihe ein Regal von der Stirnseite, gestapelt wie Paletten
 *  (belegt unten in Stahl, darueber wartende in Bernstein, oben die freien Faecher). */
export function Regalwand({ reihen, status }: { reihen: WandReihe[]; status: NaviStatus | null }) {
  const max = Math.max(1, ...reihen.map(r => r.plaetze))
  const hallen = ([1, 2] as const).map(nr => ({ nr, reihen: reihen.filter(r => r.lager === nr) })).filter(h => h.reihen.length)
  if (!hallen.length) return <p className="text-sm text-ink-2">Keine Plätze für diesen Filter.</p>
  return <div className="flex flex-wrap gap-x-8 gap-y-6" aria-label="Regalwand">
    {hallen.map(h => <section key={h.nr} aria-label={`Lager ${h.nr}`} className="min-w-0">
      <h3 className="text-[11px] uppercase tracking-wider text-muted mb-2">Lager {h.nr}</h3>
      <div className="flex items-end gap-1.5 overflow-x-auto pb-1 border-b-2 border-[#33414f]">
        {h.reihen.map(r => <Balken key={r.reihe} r={r} max={max} status={status} />)}
      </div>
    </section>)}
  </div>
}

function Balken({ r, max, status }: { r: WandReihe; max: number; status: NaviStatus | null }) {
  const hoehe = Math.max(28, Math.round(r.plaetze / max * HOEHE))
  const anteil = (n: number) => r.plaetze ? `${n / r.plaetze * 100}%` : '0%'
  const blass = (s: NaviStatus) => status !== null && status !== s && 'opacity-25'
  const zahl = status ? r[status] : r.frei
  return <Link to={`/warehouse/lager-navi/regale?reihe=${encodeURIComponent(r.reihe)}`}
    aria-label={`${r.reihe}: ${r.frei} frei, ${r.belegt} belegt, ${r.wartet} wartet, ${r.notiz} Notiz, ${r.plaetze} Plätze`}
    className="group flex w-9 shrink-0 flex-col items-center gap-1 rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
    <span className="text-[11px] font-semibold tabular-nums text-ink">{zahl}</span>
    <span className="flex w-full flex-col-reverse overflow-hidden rounded-t border border-[#33414f] bg-white transition-transform group-hover:-translate-y-0.5 motion-reduce:transition-none"
      style={{ height: hoehe }}>
      <span className={cn('block bg-[#33414f]', blass('belegt'))} style={{ height: anteil(r.belegt) }} />
      <span className={cn('block bg-warn-soft', blass('wartet'))} style={{ height: anteil(r.wartet), backgroundImage: SCHRAFFUR }} />
      <span className={cn('block bg-slate-100', blass('notiz'))} style={{ height: anteil(r.notiz), backgroundImage: NOTIZ_STREIFEN }} />
      <span className={cn('block flex-1 bg-[repeating-linear-gradient(0deg,transparent_0_5px,#e3e8ed_5px_6px)]', blass('frei'))} />
    </span>
    <span className="font-mono text-[11px] font-semibold text-ink-2 group-hover:text-ink">{r.reihe}</span>
  </Link>
}
